import type {
  AnalysisBreakdownAggregation,
  AnalysisSheetRecipe,
} from '@/types/analysisRecipe.types';
import type { AnalysisRelationshipPath } from '@/helpers/analysisRelationshipPath.helpers';
import {
  analysisLookupOrderBy,
  analysisStepOutputField,
  normalizeAnalysisJoinConfig,
  normalizeAnalysisLookupConfig,
  normalizeAnalysisRollupMetrics,
} from '@/helpers/analysisMadeStep.helpers';

/**
 * Why a breakdown's own grouping cannot be trusted as-is, or `undefined` when
 * it can.
 *
 * `groupByRelated` groupings are always safe — they join on the related node
 * itself rather than a lookup's collapsed value, so there is nothing arbitrary
 * to warn about. A plain column grouping is unsafe exactly when that column is
 * a to-many (or unknown-cardinality) lookup's own output: the lookup already
 * picked one arbitrary match per row (see `buildLookupAnalysisStep`'s own
 * "(one arbitrary match; no ordering rule)" note), so the value being grouped
 * on — and therefore which group a row lands in — is not the same run to run,
 * while the group totals still look authoritative. This is a structural check
 * against the lookup's declared cardinality, not a live probe of the actual
 * data: a probe could be right today and wrong on a future run, which is a
 * worse failure mode than warning on every to-many lookup unconditionally.
 *
 * A lookup carrying an ordering rule (`config.orderBy`) is exempt whatever its
 * cardinality. It still collapses many matches to one, but *which* one is now
 * defined — earliest or latest by a named field — so the value is stable run to
 * run and the group totals mean what they appear to mean. That is precisely the
 * property this warning exists to flag the absence of.
 */
export function analysisBreakdownArbitraryGroupWarning(
  sheet: AnalysisSheetRecipe,
  parentSheet: AnalysisSheetRecipe | null,
): string | undefined {
  const breakdown = sheet.breakdown;
  // A total sheet groups by nothing, so no arbitrary value decides which group a
  // row lands in — there is only one group.
  if (
    !breakdown ||
    breakdown.groupByRelated ||
    !breakdown.groupByColumn ||
    !parentSheet
  ) {
    return undefined;
  }
  const groupByColumn = breakdown.groupByColumn;
  const step = parentSheet.steps.find((candidate) =>
    candidate.outputColumns.includes(groupByColumn),
  );
  if (step?.kind !== 'lookup') return undefined;
  // An ordered pick is repeatable, so nothing here is arbitrary.
  if (analysisLookupOrderBy(step)) return undefined;
  // Missing, not just 'unknown', reads as unproven — the same default
  // `buildLookupAnalysisStep` itself falls back to for a freshly built lookup
  // (`analysisMadeStep.helpers.ts`). Treating an absent hint as safe would be
  // a false negative on exactly the recipes most likely to lack one: older
  // saved sheets from before this field existed.
  const cardinalityHint = step.config.cardinalityHint ?? 'unknown';
  if (cardinalityHint !== 'many' && cardinalityHint !== 'unknown') {
    return undefined;
  }
  return (
    'This column comes from a to-many lookup, so each row shows one ' +
    'arbitrary match — group membership can change between runs and these ' +
    'totals should not be read as authoritative. Use "Group by related ' +
    'record" instead for a grouping that counts every match.'
  );
}

/**
 * Column-name shapes that read as an identifier rather than a measure.
 *
 * Suffix-anchored on purpose: `number_of_incidents` is a genuine count and must
 * survive, while `company_number` must not. A prefix or substring rule would
 * catch both.
 */
const IDENTIFIER_SUFFIXES = [
  '_id',
  '_ids',
  '_number',
  '_num',
  '_no',
  '_code',
  '_key',
  '_uuid',
  '_guid',
];

/**
 * Whether a numeric column is an identifier, and so meaningless to sum.
 *
 * Name-based, deliberately, after the alternatives were tested against real data:
 *
 * - **Distinctness fails.** On ATCO_v3's Actual sheet every `charges` value is as
 *   distinct as every `company_number` — currency amounts simply do not repeat —
 *   so a near-uniqueness rule would drop the sheet's primary metric along with
 *   the identifier. Uniqueness distinguishes nothing here.
 * - **Schema kind cannot help.** An identifier stored as a number *is* a number;
 *   `schemaPropertyKindsByLabel` is right about it and still useless for this
 *   question.
 *
 * That leaves the name, which is the only place the distinction is recorded. It
 * mirrors `identityFieldFor` in `AnalysisMadeStepForm`, which already reads names
 * to find a record's identity field.
 *
 * Known blind spot: a Made step's output column is an opaque generated key
 * (`analysis__<hash>__<hash>`), so an identifier pulled in by a lookup is not
 * recognised here and keeps being carried. Reading the display label would fix it
 * and is not worth the coupling — the author can remove the metric, and the
 * default is what this is about.
 */
export function isAnalysisIdentifierColumn(column: string): boolean {
  const name = column.toLowerCase();
  if (name === 'id') return true;
  return IDENTIFIER_SUFFIXES.some((suffix) => name.endsWith(suffix));
}

/**
 * The numeric columns a new grouped or total sheet should sum by default.
 *
 * Applied inside the creators rather than at their call sites so every creator —
 * including any added later — shares one answer. Excluding an identifier here
 * only changes the *default*: the author can still add it as a rollup metric
 * deliberately, which is the honest place for a deliberate choice.
 *
 * Not silently converted to `count_distinct` either. "How many distinct
 * companies" is a fair question, but nobody asked it, and answering an unasked
 * question with an auto-added column trades one unwanted metric for another.
 */
const carriedMetricsFor = (columns: string[], exclude?: string): string[] =>
  [...new Set(columns)].filter(
    (field) => field !== exclude && !isAnalysisIdentifierColumn(field),
  );

const sheetId = (): string =>
  `breakdown-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;

/**
 * Whether a sheet collapses its parent to a single total row.
 *
 * The check is "is a breakdown, has no group key of either kind" — never a bare
 * `!groupByColumn`, because a breakdown whose source column was removed is also
 * missing one and is `broken`, not a total. Grouping by a related record supplies
 * its key through `groupByRelated`, so that has to be excluded too.
 */
export function isAnalysisTotalSheet(
  sheet: AnalysisSheetRecipe | null | undefined,
): boolean {
  const breakdown = sheet?.breakdown;
  if (!breakdown) return false;
  if (breakdown.status === 'broken') return false;
  return !breakdown.groupByColumn && !breakdown.groupByRelated;
}

/**
 * A sheet that totals its parent to one row.
 *
 * Reuses the breakdown link rather than inventing a sibling concept, so family
 * scoping, view-state keying and the discard cascade all treat it as the derived
 * sheet it is. Its count is a plain `count`, not `count_distinct`: there is no
 * join fan-out to dedupe at this grain, and `count_distinct` would cost a sort
 * for an identical answer.
 */
export function createAnalysisTotalSheet(
  parent: AnalysisSheetRecipe,
  /** Numeric columns to total alongside the row count. */
  carriedMetricColumns: string[] = [],
): AnalysisSheetRecipe {
  const id = sheetId();
  const countColumn = analysisStepOutputField(id, 'count');
  const carriedMetrics = carriedMetricsFor(
    carriedMetricColumns,
  ).map<AnalysisBreakdownAggregation>((field) => ({
    id: field,
    field,
    op: 'sum',
    outputColumn: analysisStepOutputField(id, `${field}-summary`),
  }));
  return {
    id,
    name: `${parent.name} — total`,
    face: 'analysis',
    source: parent.source,
    worksetRootSheetId: parent.worksetRootSheetId,
    steps: [],
    keepFilter: null,
    controls: [],
    breakdown: {
      parentSheetId: parent.id,
      // No `groupByColumn` at all — its absence is what makes this a total.
      status: 'live',
      aggregations: [
        {
          id: 'count',
          op: 'count',
          outputColumn: countColumn,
        },
        ...carriedMetrics,
      ],
      removedAggregations: [],
      windows: [],
    },
    charts: [],
  };
}

export function createAnalysisBreakdownSheet(
  parent: AnalysisSheetRecipe,
  groupByColumn: string,
  /** Human label for the grouped column; the raw key is internal. */
  groupByLabel?: string,
  /** Numeric columns deliberately carried into the grouped output. */
  carriedMetricColumns: string[] = [],
): AnalysisSheetRecipe {
  const id = sheetId();
  const countColumn = analysisStepOutputField(id, 'count');
  const carriedMetrics = carriedMetricsFor(
    carriedMetricColumns,
    groupByColumn,
  ).map<AnalysisBreakdownAggregation>((field) => ({
    id: field,
    field,
    op: 'sum',
    outputColumn: analysisStepOutputField(id, `${field}-summary`),
  }));
  return {
    id,
    name: `${parent.name} by ${groupByLabel ?? groupByColumn}`,
    face: 'analysis',
    source: parent.source,
    worksetRootSheetId: parent.worksetRootSheetId,
    steps: [],
    keepFilter: null,
    controls: [],
    breakdown: {
      parentSheetId: parent.id,
      groupByColumn,
      status: 'live',
      aggregations: [
        {
          id: 'count',
          op: 'count',
          outputColumn: countColumn,
        },
        ...carriedMetrics,
      ],
      removedAggregations: [],
      windows: [],
    },
    charts: [],
  };
}

/**
 * A breakdown grouped by the identity of a related record reached via `path`,
 * rather than by a column value.
 *
 * Grouping by a lookup's own output is unsafe once the path is one-to-many:
 * the lookup already collapsed each row's matches to one arbitrary pick
 * (`first`), so the value it would group on — and therefore which group a row
 * lands in — is not the same run to run. This groups on the related node
 * itself instead: the join keeps every match, a row is counted in every group
 * it actually reaches (so group totals legitimately exceed the row count),
 * and a row with no match becomes its own "No <targetLabel>" group instead of
 * a silently arbitrary or blank one.
 */
export function createAnalysisRelatedBreakdownSheet(
  parent: AnalysisSheetRecipe,
  path: AnalysisRelationshipPath,
  displayField: string,
  targetLabel: string,
  /** Numeric columns deliberately carried into the grouped output. */
  carriedMetricColumns: string[] = [],
): AnalysisSheetRecipe {
  const id = sheetId();
  const groupByColumn = analysisStepOutputField(id, 'group');
  const countColumn = analysisStepOutputField(id, 'count');
  const carriedMetrics = carriedMetricsFor(
    carriedMetricColumns,
  ).map<AnalysisBreakdownAggregation>((field) => ({
    id: field,
    field,
    op: 'sum',
    outputColumn: analysisStepOutputField(id, `${field}-summary`),
  }));
  return {
    id,
    name: `${parent.name} by ${targetLabel}`,
    face: 'analysis',
    source: parent.source,
    worksetRootSheetId: parent.worksetRootSheetId,
    steps: [],
    keepFilter: null,
    controls: [],
    breakdown: {
      parentSheetId: parent.id,
      groupByColumn,
      groupByRelated: { path, displayField, targetLabel },
      status: 'live',
      aggregations: [
        {
          id: 'count',
          // Distinct source count, not a plain row count — see the doc on
          // `AnalysisBreakdownAggregation['op']` and the compiler's own
          // comment at its `count_distinct` branch for why the two differ
          // once the join fans a source out to more than one group.
          op: 'count_distinct',
          outputColumn: countColumn,
        },
        ...carriedMetrics,
      ],
      removedAggregations: [],
      windows: [],
    },
    charts: [],
  };
}

/**
 * `avg` joined the editable set with PD-113. It was absent because the column
 * menu never offered it, not because the compiler could not emit it — the
 * creators have always been able to produce one, and the menu was coercing a
 * saved `avg` to read as `sum`.
 */
type EditableBreakdownOperation = 'count' | 'sum' | 'avg' | 'min' | 'max';

export function setAnalysisBreakdownMetricOperation(
  sheet: AnalysisSheetRecipe,
  outputColumn: string,
  operation: EditableBreakdownOperation,
): AnalysisSheetRecipe {
  if (!sheet.breakdown) return sheet;
  const aggregations = sheet.breakdown.aggregations ?? [];
  const target = aggregations.find(
    (aggregation) => aggregation.outputColumn === outputColumn,
  );
  if (!target?.field) return sheet;
  return {
    ...sheet,
    breakdown: {
      ...sheet.breakdown,
      aggregations: aggregations.map((aggregation) =>
        aggregation.outputColumn === outputColumn
          ? { ...aggregation, op: operation }
          : aggregation,
      ),
    },
  };
}

export function removeAnalysisBreakdownMetric(
  sheet: AnalysisSheetRecipe,
  outputColumn: string,
): AnalysisSheetRecipe {
  if (!sheet.breakdown) return sheet;
  const aggregations = sheet.breakdown.aggregations ?? [];
  const target = aggregations.find(
    (aggregation) => aggregation.outputColumn === outputColumn,
  );
  if (!target?.field) return sheet;
  // A share or rank reads this metric's column, so it cannot outlive it: leaving
  // one behind made the pipeline project a column that no longer existed, and the
  // run failed with the orphan's raw key showing as its heading.
  const windows = sheet.breakdown.windows ?? [];
  const orphaned = windows.filter(
    (window) => window.sourceColumn === outputColumn,
  );
  return {
    ...sheet,
    breakdown: {
      ...sheet.breakdown,
      aggregations: aggregations.filter(
        (aggregation) => aggregation.outputColumn !== outputColumn,
      ),
      removedAggregations: [
        ...(sheet.breakdown.removedAggregations ?? []).filter(
          (aggregation) => aggregation.outputColumn !== outputColumn,
        ),
        target,
      ],
      windows: windows.filter((window) => window.sourceColumn !== outputColumn),
      removedWindows: [
        ...(sheet.breakdown.removedWindows ?? []).filter(
          (window) => !orphaned.some((dropped) => dropped.id === window.id),
        ),
        ...orphaned,
      ],
    },
  };
}

export function restoreAnalysisBreakdownMetric(
  sheet: AnalysisSheetRecipe,
  outputColumn: string,
): AnalysisSheetRecipe {
  if (!sheet.breakdown) return sheet;
  const removed = sheet.breakdown.removedAggregations ?? [];
  const target = removed.find(
    (aggregation) => aggregation.outputColumn === outputColumn,
  );
  if (!target) return sheet;
  // Bring back the shares and ranks that were archived with it, so an undo
  // restores the columns the user actually had.
  const removedWindows = sheet.breakdown.removedWindows ?? [];
  const restoredWindows = removedWindows.filter(
    (window) => window.sourceColumn === outputColumn,
  );
  return {
    ...sheet,
    breakdown: {
      ...sheet.breakdown,
      aggregations: [...(sheet.breakdown.aggregations ?? []), target],
      removedAggregations: removed.filter(
        (aggregation) => aggregation.outputColumn !== outputColumn,
      ),
      windows: [...(sheet.breakdown.windows ?? []), ...restoredWindows],
      removedWindows: removedWindows.filter(
        (window) => window.sourceColumn !== outputColumn,
      ),
    },
  };
}

/**
 * Aggregations a breakdown can apply to a source column.
 *
 * `avg` is offered on numeric columns: the `averageRollup` capability the
 * compiler gates is about averaging *inside a rollup*, where Memgraph rejects
 * the `CASE`-wrapped aggregate. A breakdown's `avg` compiles to a plain
 * `group_rows` aggregation, which the backend accepts.
 *
 * Which operations are safe for which column is decided here rather than
 * server-side — see `NON_NUMERIC_SUMMARY_OPERATIONS` for why nothing in the
 * backend catches a bad one.
 */
export type AnalysisSummaryOperation = 'count' | 'sum' | 'avg' | 'min' | 'max';

/** Every operation, for a column proven to hold numbers. */
const NUMERIC_SUMMARY_OPERATIONS: AnalysisSummaryOperation[] = [
  'count',
  'sum',
  'avg',
  'min',
  'max',
];

/**
 * What is left once the coercing operations are withheld: counting.
 *
 * `min` and `max` were here too, on the assumption that ordering text or dates
 * is meaningful. It is not what the backend does. A breakdown aggregation is
 * emitted as `col`, and every `col` arithmetic op — `sum`, `avg`, `min`, `max`
 * alike — compiles to a `toFloat()` wrap (`review_mcp`
 * `graph_algebra/cypher/_aggregations.py`, the `min`/`max` branch: "toFloat-
 * coerce so numeric-string fields order numerically"). Over text that yields
 * null for every row, so the column renders blank with no error; over a Date,
 * Memgraph raises `Invalid input for function 'toFloat()'` and the whole sheet
 * fails to run, not just that column.
 *
 * The `toFloat` wrap is also why a number stored as text is fine: it coerces.
 * So the real question for all four operations is whether the values parse as
 * numbers, which is exactly what `analysisColumnIsNumeric` answers.
 *
 * (An earlier version of this comment cited the backend's
 * `_validate_numeric_aggs` as the guard. That was wrong twice over: the
 * function has no production call sites — only tests reference it — and it
 * skips any aggregation without a `field`, which every breakdown aggregation
 * is. Nothing server-side stops a bad `col` aggregation; this list is the
 * guard.)
 */
const NON_NUMERIC_SUMMARY_OPERATIONS: AnalysisSummaryOperation[] = ['count'];

/**
 * Whether every non-empty sampled value parses as a finite number —
 * `undefined` when there was nothing to sample.
 *
 * The same rule `createBreakdownForColumn` applies when deciding which columns
 * a new grouped sheet carries, so the metrics a breakdown starts with and the
 * operations its menu later offers cannot disagree.
 */
const sampledNumeric = (
  column: string,
  rows: Array<Record<string, unknown>> | undefined,
): boolean | undefined => {
  if (!rows?.length) return undefined;
  const values = rows
    .map((row) => row[column])
    .filter((value) => value !== null && value !== undefined && value !== '');
  if (values.length === 0) return undefined;
  return values.every((value) => Number.isFinite(Number(value)));
};

/**
 * Whether a source column holds numbers — `undefined` when it cannot be decided.
 *
 * Decided structurally, from the recipe and the project schema, rather than by
 * sniffing the rows on screen: on a grouped sheet's Analysis face the rows are
 * the *grouped* ones, so the source column being asked about has no values
 * there at all. A rule that only worked on the Data face would silently offer a
 * different set of operations depending on which face the menu was opened from.
 *
 * The three-valued return is deliberate — see `analysisSummaryOperationsFor`
 * for what "cannot be decided" is allowed to mean.
 */
export function analysisColumnIsNumeric(
  column: string,
  context: {
    /** The sheet whose grain the column belongs to. */
    sourceSheet?: AnalysisSheetRecipe | null;
    /** The source sheet's own label, for raw properties. */
    sourceLabel?: string | null;
    schemaPropertyKindsByLabel?: Record<
      string,
      Record<string, string | undefined> | undefined
    >;
    /**
     * Source-grain rows for the label, used only as *positive* evidence about a
     * raw property the schema calls text.
     *
     * Numbers stored as strings are common enough that refusing to sum them
     * would fail the very columns authors most want summed, and the backend
     * agrees: it auto-promotes `sum`/`avg` to their `_numeric` forms for a
     * String-only column rather than erroring. Sniffing never overrides a
     * `date`, `boolean` or `other` verdict — the backend rejects temporal
     * `sum`/`avg` outright, so a date that parses as a number must stay
     * non-numeric here.
     */
    sampleRows?: Array<Record<string, unknown>>;
  },
): boolean | undefined {
  const kinds = context.schemaPropertyKindsByLabel ?? {};
  const kindOf = (label: string | null | undefined, field: string) => {
    if (!label) return undefined;
    return kinds[label]?.[field];
  };
  const step = context.sourceSheet?.steps.find((candidate) =>
    candidate.outputColumns.includes(column),
  );
  if (!step) {
    const kind = kindOf(context.sourceLabel, column);
    if (kind === 'number') return true;
    if (kind !== undefined && kind !== 'text') return false;
    const sampled = sampledNumeric(column, context.sampleRows);
    if (sampled !== undefined) return sampled;
    // No values to judge by: text is still text, and an unknown property stays
    // unknown rather than inheriting a verdict from nothing.
    return kind === 'text' ? false : undefined;
  }
  if (step.kind === 'rollup') {
    // Counting and arithmetic always return a number. `min`/`max` return a
    // value *of the rolled-up column's own type* — `max` over a Date is a Date
    // — so a blanket "every rollup is numeric" offered `sum` on a date column
    // and the run failed. A rollup can carry several metrics, each with its own
    // output column, so the verdict is per column, not per step.
    const metric = normalizeAnalysisRollupMetrics(step).find(
      (candidate) => candidate.outputColumn === column,
    );
    if (!metric) return undefined;
    if (metric.metric !== 'min' && metric.metric !== 'max') return true;
    // A min/max rollup is numeric exactly when the column it read is. The
    // target label is the one the rollup reached, not the sheet's own.
    const targetLabel =
      typeof step.config.targetLabel === 'string'
        ? step.config.targetLabel
        : null;
    if (!metric.field) return undefined;
    const kind = kindOf(targetLabel, metric.field);
    if (kind === undefined) return undefined;
    return kind === 'number';
  }
  if (step.kind === 'calc') {
    const operator = step.config.operator;
    // A "Flag rows" calc carries a `flagCondition` and no operator, and emits
    // the literal strings 'Yes' and 'No'. It has to be named before the operator
    // checks: falling through to the arithmetic default declared it numeric, so
    // the menu offered Sum and Average over 'Yes'/'No' and the backend's
    // `sum(toFloat(...))` returned an empty column with no error — the same
    // silent-blank failure min/max was withdrawn for, reached by another route.
    if (step.config.flagCondition !== undefined || operator === undefined) {
      return false;
    }
    if (operator === 'concat') return false;
    // `coalesce` returns whichever operand was first non-empty, so its type is
    // whatever those operands are. Not worth resolving transitively for the one
    // operator it affects; unknown is the honest answer.
    if (operator === 'coalesce') return undefined;
    return true;
  }
  if (step.kind === 'lookup') {
    const config = normalizeAnalysisLookupConfig(step);
    const targetLabel =
      typeof step.config.targetLabel === 'string'
        ? step.config.targetLabel
        : null;
    const outputs = config.configuredOutputs ?? [
      ...config.intermediateOutputs.map((entry, index) => ({
        outputColumn: entry.outputColumn ?? step.outputColumns[index],
        field: entry.field,
        hopIndex: entry.hopIndex,
      })),
      ...config.targetFields.map((field, index) => ({
        outputColumn:
          step.outputColumns[config.intermediateOutputs.length + index],
        field,
        hopIndex: null,
      })),
    ];
    const output = outputs.find((entry) => entry.outputColumn === column);
    if (!output) return undefined;
    const owner =
      output.hopIndex !== null && output.hopIndex >= 0
        ? (config.path.hops[output.hopIndex]?.targetLabel ?? targetLabel)
        : targetLabel;
    const kind = kindOf(owner, output.field);
    if (kind === undefined) return undefined;
    return kind === 'number';
  }
  if (step.kind === 'join') {
    // A joined field's type is the target label's own, exactly as for a lookup.
    // Without this a joined number fell through to `undefined` and was treated
    // as non-numeric, so Sum and Average were withheld from a column that is a
    // number — which reads as the join half-working rather than as caution.
    const config = normalizeAnalysisJoinConfig(step);
    const output = config?.outputs.find(
      (entry) => entry.outputColumn === column,
    );
    if (!config || !output) return undefined;
    const kind = kindOf(config.targetLabel, output.field);
    if (kind === undefined) return undefined;
    return kind === 'number';
  }
  return undefined;
}

/**
 * The operations to offer for a column, given what is known about its type.
 *
 * An undecidable column is treated as non-numeric on purpose. The backend
 * refuses `sum`/`avg` over "genuinely-unknown / mixed types" rather than
 * guessing, so offering them would put a failing run behind a menu entry; the
 * author can still reach the number through a rollup on the source sheet, which
 * is a deliberate choice rather than a default.
 *
 * `currentOperation` is kept in the list whatever the verdict, so a saved sheet
 * carrying an `avg` on a column this now reads as non-numeric still shows what
 * it is doing instead of rendering with nothing selected.
 */
export function analysisSummaryOperationsFor(
  isNumeric: boolean | undefined,
  currentOperation?: AnalysisSummaryOperation,
): AnalysisSummaryOperation[] {
  const base =
    isNumeric === true
      ? NUMERIC_SUMMARY_OPERATIONS
      : NON_NUMERIC_SUMMARY_OPERATIONS;
  if (currentOperation && !base.includes(currentOperation)) {
    return [...base, currentOperation];
  }
  return [...base];
}

/** Source fields a breakdown already summarizes, live or archived. */
export function analysisBreakdownSummarizedFields(
  sheet: AnalysisSheetRecipe | null | undefined,
): Set<string> {
  const breakdown = sheet?.breakdown;
  if (!breakdown) return new Set();
  return new Set(
    [
      ...(breakdown.aggregations ?? []),
      ...(breakdown.removedAggregations ?? []),
    ].flatMap((aggregation) => (aggregation.field ? [aggregation.field] : [])),
  );
}

/**
 * Adds a summary of `field` to a grouped sheet, or retargets one that is
 * already there.
 *
 * Add and restore converge deliberately. The output column is derived from the
 * sheet id and the field name, exactly as the creators derive it, so a field
 * that was removed earlier has an archived aggregation under the very key this
 * would mint. Appending a second one would leave the pipeline projecting the
 * same name twice and the restore chip offering to bring back a column that is
 * already on screen.
 */
export function addAnalysisBreakdownMetric(
  sheet: AnalysisSheetRecipe,
  field: string,
  operation: AnalysisSummaryOperation,
): AnalysisSheetRecipe {
  if (!sheet.breakdown || !field) return sheet;
  const live = sheet.breakdown.aggregations ?? [];
  const existing = live.find((aggregation) => aggregation.field === field);
  if (existing) {
    return setAnalysisBreakdownMetricOperation(
      sheet,
      existing.outputColumn,
      operation,
    );
  }
  const archived = (sheet.breakdown.removedAggregations ?? []).find(
    (aggregation) => aggregation.field === field,
  );
  if (archived) {
    return setAnalysisBreakdownMetricOperation(
      restoreAnalysisBreakdownMetric(sheet, archived.outputColumn),
      archived.outputColumn,
      operation,
    );
  }
  return {
    ...sheet,
    breakdown: {
      ...sheet.breakdown,
      aggregations: [
        ...live,
        {
          id: field,
          field,
          op: operation,
          outputColumn: analysisStepOutputField(sheet.id, `${field}-summary`),
        },
      ],
    },
  };
}
